import { BoolSchema } from "./schema/type/BoolSchema";
import { IntegerSchema } from "./schema/type/IntegerSchema";
import { FloatSchema } from "./schema/type/FloatSchema";
import { StringSchema } from "./schema/type/StringSchema";
import { DateSchema } from "./schema/type/DateSchema";
import { ArraySchema } from "./schema/type/ArraySchema";
import { ObjectSchema } from "./schema/type/ObjectSchema";
import { AnySchema } from "./schema/type/AnySchema";
import { TinyIntSchema } from "./schema/type/TinyIntSchema";
import { SmallIntSchema } from "./schema/type/SmallIntSchema";
import { MediumIntSchema } from "./schema/type/MediumIntSchema";
import { BigIntSchema } from "./schema/type/BigIntSchema";
import { DoubleSchema } from "./schema/type/DoubleSchema";
import { DecimalSchema } from "./schema/type/DecimalSchema";
import { RealSchema } from "./schema/type/RealSchema";
import { DateTimeSchema } from "./schema/type/DateTimeSchema";
import { TimeSchema } from "./schema/type/TimeSchema";
import { EnumSchema } from "./schema/type/EnumSchema";
import { BaseTypeBuilder } from "./BaseTypeBuilder";
import { BaseTypeSchema } from "./schema/BaseTypeSchema";
import { BaseNumberSchema } from "./schema/type/BaseNumberSchema";
import { EmailSchema } from "./schema/type/EmailSchema";
import { PhoneSchema } from "./schema/type/PhoneSchema";
import { IPV4Schema } from "./schema/type/IPV4Schema";
import { IPV6Schema } from "./schema/type/IPV6Schema";
import { DurationSchema } from "./schema/type/DurationSchema";
import { MoneySchema } from "./schema/type/MoneySchema";
import { TimeZoneSchema } from "./schema/type/TimeZoneSchema";
import { IPV4RangeSchema } from "./schema/type/IPV4RangeSchema";
import { IPV6RangeSchema } from "./schema/type/IPV6RangeSchema";
import { ColorSchema } from "./schema/type/ColorSchema";
import { URLSchema } from "./schema/type/URLSchema";
import { FilePathSchema } from "./schema/type/FilePathSchema";
import { FontSchema } from "./schema/type/FontSchema";
import { EntitySchema } from "./schema/type/EntitySchema";
import { VersionSchema } from "./schema/type/VersionSchema";
import { TypeSchema } from "./schema/type/TypeSchema";
import { VariableSchema } from "./schema/type/VariableSchema";

export class SwaggerTypeBuilder extends BaseTypeBuilder<void>
{
    private useClassName: boolean;
    private lines: string[];
    private lineAdded: boolean = false;
    private indent: string;
    private firstLineIndentOnly?: string;
    constructor(schema: BaseTypeSchema | null, useClassName: boolean, lines: string[], indent: string, firstLineIndentOnly?: string)
    {
        super(schema);
        this.useClassName = useClassName;
        this.lines = lines;
        this.indent = indent;
        this.firstLineIndentOnly = firstLineIndentOnly;
    }
    private addLine(value: string)
    {
        let indentFinal = this.indent;
        if (!this.lineAdded)
            if (this.firstLineIndentOnly)
                indentFinal = this.firstLineIndentOnly;
        this.lineAdded = true;
        this.lines.push(indentFinal + value);
    }
    /* VariableType */
    protected override _toAny(schema: AnySchema)
    {
        if (schema.required)
        {
            this.addLine("AnyValue: {}");
        }
        else
        {
            this.addLine("AnyValue: ");
            this.addLine(`  nullable: true`);
        }
    }
    private toBase(schema: BaseTypeSchema, type: string | null, format: string | null, minName: string | null, maxName: string | null, min: number | null, max: number | null)
    {
        if (type)
            this.addLine(`type: ${type}`);
        if (format)
            this.addLine(`format: ${format}`);
        if (!schema.required)
            this.addLine(`nullable: true`);
        //
        if (min != null)
            this.addLine(`${minName}: ${min}`);
        if (max != null)
            this.addLine(`${maxName}: ${max}`);
        //
        if (schema.valids.length != 0)
            this.addLine(`enum: [${schema.valids.join(", ")}]`);
        if (schema.example)
            this.addLine(`example: ${schema.example}`);
        // if (schema.read_only)
        //     this.addLine(`readOnly: true`);
    }
    private toBaseNumber(schema: BaseNumberSchema, type: string, format: string | null)
    {
        this.toBase(schema, type, format, "minimum", "maximum", schema.min, schema.max);
    }
    private toBaseString(schema: BaseTypeSchema, type: string, format: string | null, min: number | null, max: number | null, regex?: RegExp)
    {
        this.toBase(schema, type, format, "minLength", "maxLength", min, max);
        if (regex)
            this.addLine(`pattern: ${regex}`);
    }
    protected override _toBool(schema: BoolSchema)
    {
        this.toBase(schema, "boolean", null, null, null, null, null);
    }
    protected override _toTinyInt(schema: TinyIntSchema)
    {
        this.toBaseNumber(schema, "integer", "int4");
    }
    protected override _toSmallInt(schema: SmallIntSchema)
    {
        this.toBaseNumber(schema, "integer", "int8");
    }
    protected override _toMediumInt(schema: MediumIntSchema)
    {
        this.toBaseNumber(schema, "integer", "int16");
    }
    protected override _toInteger(schema: IntegerSchema)
    {
        this.toBaseNumber(schema, "integer", "int32");
    }
    protected override _toBigInt(schema: BigIntSchema)
    {
        this.toBaseNumber(schema, "integer", "int64");
    }
    protected override _toFloat(schema: FloatSchema)
    {
        this.toBaseNumber(schema, "number", "float");
    }
    protected override _toDouble(schema: DoubleSchema)
    {
        this.toBaseNumber(schema, "number", "double");
    }
    protected override _toDecimal(schema: DecimalSchema)
    {
        this.toBaseNumber(schema, "number", "double");
    }
    protected override _toReal(schema: RealSchema)
    {
        this.toBaseNumber(schema, "number", "double");
    }
    protected override _toMoney(schema: MoneySchema)
    {
        this.toBaseNumber(schema, "number", "double");
    }
    protected override _toString(schema: StringSchema)
    {
        let min_length = schema.min_length;
        if (schema.required)
            min_length = Math.max(min_length ?? 1, 1);
        this.toBaseString(schema, "string", null, min_length, schema.max_length, schema.regex);
    }
    protected override _toDate(schema: DateSchema)
    {
        this.toBaseString(schema, "string", "date", null, null);
    }
    protected override _toTime(schema: TimeSchema)
    {
        this.toBaseString(schema, "string", "time", null, null, TimeSchema.REGEX);
    }
    protected override _toDateTime(schema: DateTimeSchema)
    {
        this.toBaseString(schema, "string", "date-time", null, null);
    }
    protected override _toTimeZone(schema: TimeZoneSchema)
    {
        this.toBaseString(schema, "string", "time-zone", null, null);
    }
    protected override _toDuration(schema: DurationSchema)
    {
        this.toBaseString(schema, "string", "time", null, null, DurationSchema.REGEX);
    }
    protected override _toEmail(schema: EmailSchema)
    {
        this.toBaseString(schema, "string", "email", null, null);
    }
    protected override _toPhone(schema: PhoneSchema)
    {
        this.toBaseString(schema, "string", "phone", null, null, PhoneSchema.REGEX);
    }
    protected override _toVersion(schema: VersionSchema)
    {
        this.toBaseString(schema, "string", "version", null, null, VersionSchema.REGEX);
    }
    protected override _toIPV4(schema: IPV4Schema)
    {
        this.toBaseString(schema, "string", "ipv4", null, null, IPV4Schema.REGEX);
    }
    protected override _toIPV6(schema: IPV6Schema)
    {
        this.toBaseString(schema, "string", "ipv6", null, null, IPV6Schema.REGEX);
    }
    protected override _toIPV4Range(schema: IPV4RangeSchema)
    {
        this.toBaseString(schema, "string", "ipv4-range", null, null, IPV4RangeSchema.REGEX);
    }
    protected override _toIPV6Range(schema: IPV6RangeSchema)
    {
        this.toBaseString(schema, "string", "ipv6-range", null, null, IPV6RangeSchema.REGEX);
    }
    protected override _toColor(schema: ColorSchema)
    {
        this.toBaseString(schema, "string", "color", null, null);
    }
    protected override _toFont(schema: FontSchema)
    {
        this.toBaseString(schema, "string", "color", null, null);
    }
    protected override _toFilePath(schema: FilePathSchema)
    {
        this.toBaseString(schema, "string", "color", null, null);
    }
    protected override _toURL(schema: URLSchema)
    {
        this.toBaseString(schema, "string", "url", null, null);
    }
    protected override _toEnum(schema: EnumSchema)
    {
        this.toBaseString(schema, "string", "enum", null, null);
    }
    protected override _toEntity(schema: EntitySchema)
    {
        this.toBaseString(schema, "string", "url", null, null, schema.getRegex());
    }
    protected override _toArray(schema: ArraySchema, callback: (schema: BaseTypeSchema) => void)
    {
        this.toBase(schema, "array", null, "minItems", "maxItems", schema.min, schema.max);
        if (schema.items.length == 0)
            this.addLine(`items: {}`);
        else
        {
            this.addLine(`items:`);
            if (schema.items.length == 1)
            {
                let builder = new SwaggerTypeBuilder(schema.items[0], true, this.lines, this.indent + "  ");
                builder.runSynch(callback);
            }
            else
            {
                this.addLine(`  oneOf:`);
                for (let i = 0; i < schema.items.length; i++)
                {
                    const item = schema.items[i];
                    let builder = new SwaggerTypeBuilder(item, true, this.lines, this.indent + "      ", this.indent + "    - ");
                    builder.runSynch(callback);
                }
            }
        }
    }
    protected override _toObject(schema: ObjectSchema, callback: (schema: BaseTypeSchema) => void)
    {
        if (this.useClassName && schema.name)
            this.addLine(`$ref: '#/components/schemas/${schema.name}'`);
        else
        {
            this.toBase(schema, "object", null, null, null, null, null);
            if (schema.external != null || schema.fields == null)
                this.addLine(`additionalProperties: true`);
            else 
            {
                if (schema.fields.length == 0)
                {
                    this.addLine(`properties: {}`);
                }
                else
                {
                    this.addLine(`properties:`);
                    for (let i = 0; i < schema.fields.length; i++)
                    {
                        const f = schema.fields[i];
                        this.addLine(`  ${f.name}:`);
                        let builder = new SwaggerTypeBuilder(f.type, true, this.lines, this.indent + "    ");
                        builder.runSynch(callback);
                    }
                    let rs = schema.fields.filter(f => f.type.required);
                    if (rs.length > 0)
                    {
                        this.addLine(`required:`);
                        for (let i = 0; i < rs.length; i++)
                            this.addLine(`  - ${rs[i].name}`);
                    }
                }
            }
        }
    }
    protected override _toType(schema: TypeSchema)
    {
        return this._toAny(schema);
    }
    protected override _toVariable(schema: VariableSchema)
    {
        return this._toAny(schema);
    }
}